import { useInfiniteQuery } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import ms from 'ms';

import { getComments } from 'hb-react/frontoffice/apps/CommentsV2/requests/getComments';
import {
  type CommentsQueryData,
  type GetCommentsParams,
  type GetCommentsResponse,
  type PageParamType,
} from 'hb-react/frontoffice/apps/CommentsV2/types';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';

export const useComments = ({
  queryKey,
  params,
  enabled = true,
  onFocusedCommentNotFound,
}: {
  enabled?: boolean;
  onFocusedCommentNotFound?: (error: AxiosError) => void;
  params: GetCommentsParams;
  queryKey: Array<unknown>;
}) => {
  const { currentUser } = useDefaultAppContexts();
  const userId = currentUser?.id as number;
  const { focusedCommentId } = params;

  return useInfiniteQuery<
    GetCommentsResponse,
    AxiosError,
    CommentsQueryData,
    Array<unknown>,
    PageParamType
  >({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      try {
        return await getComments({
          userId,
          params,
          pageParam,
          signal,
        });
      } catch (error) {
        if (focusedCommentId) {
          onFocusedCommentNotFound?.(error);
        }

        throw error;
      }
    },
    enabled,
    // Focused Comment ID is stored in page params, so it can be used during re-fetching after invalidation.
    initialPageParam: { focusedCommentId },
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page
        ? { afterCursor: lastPage.end_cursor, focusedCommentId: undefined }
        : undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage.has_previous_page
        ? { beforeCursor: firstPage.start_cursor, focusedCommentId: undefined }
        : undefined,
    staleTime: ms('5m'),
  });
};
